import { Opportunity } from '../../types';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Calendar, DollarSign, Layers, Target } from 'lucide-react';

interface OpportunityCardProps {
  opportunity: Opportunity;
}

export function OpportunityCard({ opportunity }: OpportunityCardProps) {
  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: 'open' | 'won' | 'lost') => {
    switch (status) {
      case 'won':
        return <Badge variant="approved">Ganho</Badge>;
      case 'lost':
        return <Badge variant="rejected">Perdido</Badge>;
      default:
        return <Badge variant="active">Aberto</Badge>;
    }
  };

  return (
    <Card className="shadow-sm">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm truncate">
              {opportunity.title || `Oportunidade #${opportunity.piperunDealId}`}
            </h4>
            {opportunity.pipelineName && (
              <p className="text-xs text-muted-foreground truncate">
                {opportunity.pipelineName}
              </p>
            )}
          </div>
          {getStatusBadge(opportunity.status)}
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          {opportunity.stageName && (
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">{opportunity.stageName}</span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{formatCurrency(opportunity.value)}</span>
          </div>

          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              {formatDate(opportunity.dealCreatedAt || opportunity.createdAt)}
            </span>
          </div>

          {opportunity.piperunDealId && (
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">#{opportunity.piperunDealId}</span>
            </div>
          )}
        </div>

        {(opportunity.wonAt || opportunity.lostAt) && (
          <div className="pt-2 border-t text-xs text-muted-foreground">
            {opportunity.wonAt && (
              <span>Ganho em: {formatDate(opportunity.wonAt)}</span>
            )}
            {opportunity.lostAt && (
              <span>Perdido em: {formatDate(opportunity.lostAt)}</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
